import React, { useRef, useEffect, useMemo, useCallback } from "react";

import { useLocalForage } from "Hooks/useLocalForage";
import { search, selectSearchQuery } from "Reducers/app/search";
import { useGetFilteredOrganizationProjects } from "Reducers/editingMember/organizationProjects/hooks/useGetFilteredOrganizationProjects";
import useProvisioningPuller from "Reducers/organization/projects/hooks/useProvisioningPuller";
import ProjectFilterBar from "src/organization/common/components/ProjectFilterBar";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import { CreateFirstProject } from "./containers/EmptyProjects";
import { FilterBadges } from "./containers/Projects/FilterBadges";
import { Filters } from "./containers/Projects/Filters";
import Projects from "./containers/Projects/Projects";
import * as S from "./Page.styles";

import type { Organization } from "@packages/client";
import type { GetOrganizationProjectsParams } from "Api/organizations/projects/getOrganizationProjects.request";

type Props = {
  organization: Organization;
};

const ORGANIZATION_PROJECT_SORT_TYPES = [
  { id: "project_title", ascend: "A-Z", descend: "Z-A" },
  { id: "project_region", ascend: "A-Z", descend: "Z-A" },
  {
    id: "created_at",
    ascend: "recent last",
    descend: "recent first"
  },
  { id: "plan", ascend: "D-XXL", descend: "XXL-D" },
  { id: "project_id", ascend: "0 - 1", descend: "1 - 0" }
];

type FilterConfig = {
  layout: string;
  sort: {
    key: string;
    direction: string;
  };
  isVisible: boolean;
  filters: {
    type?: string;
    region?: Array<string>;
    plans?: Array<{ id: string; label: string }>;
  };
};

const defaultConfig: FilterConfig = {
  layout: "grid",
  sort: { key: "project_title", direction: "ascend" },
  isVisible: false,
  filters: {}
};

const useOrganizationProjectsConfig = () => {
  const {
    value: config = defaultConfig,
    setValue: setConfig,
    error,
    isLoading: isLoadingConfig
  } = useLocalForage<FilterConfig>("organization_projects_filter_config");

  const finalConfig = useMemo(() => {
    if (isLoadingConfig) return undefined;
    if (error) return undefined;
    return config || defaultConfig;
  }, [isLoadingConfig, config, error]);

  return [finalConfig, setConfig, isLoadingConfig] as const;
};

const sortKeyToAPIKey = {
  project_title: "title",
  project_region: "region",
  created_at: "created_at",
  plan: "plan",
  project_id: "id"
};

const OrganizationProjects = (props: Props) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const dispatch = useAppDispatch();
  const previousSearch = useRef<string | undefined>(undefined);
  const title = useAppSelector(selectSearchQuery);
  const [config, setConfig, isLoadingConfig] = useOrganizationProjectsConfig();
  const [
    projects,
    load,
    isLoadingProjects,
    getMoreProjects,
    ,
    hasMore,
    isLoadingMore,
    facets,
    count
  ] = useGetFilteredOrganizationProjects(props.organization.id);
  const { value: deletedProjectsIds } =
    useLocalForage<Array<string>>("deletedProjectIds");
  const projectsWithoutDeletedProjects = projects.filter(
    project => !deletedProjectsIds?.includes(project.id)
  );

  const resetFilters = useCallback(() => {
    config && setConfig({ ...config, filters: {} });
    dispatch(search(""));
  }, [dispatch, config, setConfig]);

  const configSort = config?.sort;
  const configFilters = config?.filters;
  const filters: GetOrganizationProjectsParams | undefined = useMemo(() => {
    if (!configSort || !configFilters) return;
    const apiSort = `${configSort.direction === "descend" ? "-" : ""}${
      sortKeyToAPIKey[configSort.key] || "title"
    }` as GetOrganizationProjectsParams["sort"];
    return {
      filter: {
        title: title ? { contains: title } : undefined,
        type: configFilters.type ? { eq: configFilters.type } : undefined,
        region: configFilters.region
          ? { in: configFilters.region.join(",") }
          : undefined,
        plan: configFilters?.plans
          ? { in: configFilters.plans?.map(p => p.id).join(",") }
          : undefined,
        status: { in: "active,suspended" }
      },
      sort: apiSort
    };
  }, [configFilters, configSort, title]);

  const { provisioningProject, provisioningFailureProject } =
    useProvisioningPuller({
      onFinish: () => {
        load(filters);
      }
    });

  useEffect(() => {
    if (filters) {
      // We don't want to search on every key press so we wait for the user to
      // stop typing for some time. On the other hand we want to search
      // immediately if the user clears the search input or uses other filters.
      if (previousSearch.current !== filters.filter?.title?.contains) {
        previousSearch.current = filters.filter?.title?.contains;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => load(filters), 250);
      } else {
        load(filters);
      }
    }
  }, [load, filters]);

  // We don't show the loading projects because we don't know the layout yet
  if (isLoadingConfig) return null;
  if (!config) return null;

  const hasFilters = !!Object.entries(filters?.filter || {})
    .filter(([key]) => key !== "status")
    .filter(([, value]) => !!value).length;

  if (
    projects.length === 0 &&
    !provisioningProject &&
    !hasFilters &&
    !isLoadingProjects
  )
    return <CreateFirstProject organizationName={props.organization.name} />;

  return (
    <>
      <ProjectFilterBar
        numberOfProjects={count}
        sortTypes={ORGANIZATION_PROJECT_SORT_TYPES}
        toggleLayout={value =>
          setConfig({ ...config, layout: value ? "grid" : "list" })
        }
        gridLayout={config.layout !== "list"}
        sortType={config.sort.key || "name"}
        setSortType={value =>
          setConfig({
            ...config,
            sort: { key: value, direction: config.sort.direction }
          })
        }
        sortOrder={config.sort.direction === "ascend" ? "ascend" : "descend"}
        setSortOrder={value =>
          setConfig({
            ...config,
            sort: { key: config.sort.key, direction: value }
          })
        }
        shouldShowFilters={config.isVisible}
        onShowFiltersClick={() =>
          setConfig({ ...config, isVisible: !config.isVisible })
        }
      />
      <S.ProjectListLayout>
        {config.isVisible && (
          <Filters
            organization={props.organization}
            reset={() => setConfig({ ...config, filters: {} })}
            infrastructure={config.filters.type}
            setInfrastructure={value =>
              setConfig({
                ...config,
                filters: { ...config.filters, type: value }
              })
            }
            regions={config.filters.region}
            setRegions={region =>
              setConfig({
                ...config,
                filters: {
                  ...config.filters,
                  region
                }
              })
            }
            allPlans={facets.plans}
            plans={config.filters.plans}
            setPlans={plans =>
              setConfig({
                ...config,
                filters: {
                  ...config.filters,
                  plans
                }
              })
            }
          />
        )}
        <S.ProjectsAndBadgesColumn>
          <FilterBadges
            type={config.filters.type}
            onRemoveType={() =>
              setConfig({
                ...config,
                filters: { ...config.filters, type: undefined }
              })
            }
            regions={config.filters.region}
            onRemoveRegion={region =>
              setConfig({
                ...config,
                filters: {
                  ...config.filters,
                  region: config.filters.region?.filter(r => r !== region)
                }
              })
            }
            plans={config.filters.plans}
            onRemovePlan={plan =>
              setConfig({
                ...config,
                filters: {
                  ...config.filters,
                  plans: config.filters.plans?.filter(p => p.id !== plan)
                }
              })
            }
          />
          <Projects
            projects={[
              ...(provisioningProject ? [provisioningProject] : []),
              ...(provisioningFailureProject
                ? [provisioningFailureProject]
                : []),
              ...projectsWithoutDeletedProjects
            ]}
            isLoading={isLoadingProjects}
            loadMore={getMoreProjects}
            hasMore={hasMore}
            organization={props.organization}
            resetFilters={resetFilters}
            gridLayout={config.layout !== "list"}
            shouldShowFilters={config.isVisible}
            isLoadingMore={isLoadingMore}
            refetchOrganizationProjectList={load}
          />
        </S.ProjectsAndBadgesColumn>
      </S.ProjectListLayout>
    </>
  );
};

export default React.memo(OrganizationProjects);
